import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  NgZone,
  signal,
} from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';

/**
 * Voice-accentkleur per keelpoort, 1:1 uit het Figma-design. De kleur groepeert
 * de voices per circuit-familie (geel = collectief logisch, zalm = collectief
 * abstract, oranje = individueel, roze = integratie, rood = tribaal) zodat
 * voices binnen eenzelfde circuit van elkaar te onderscheiden blijven.
 */
const HD_VOICE_CARD_COLORS: Readonly<Record<number, string>> = {
  62: '#ffe99c', 16: '#ffe99c', 31: '#ffe99c',
  56: '#ffc09c', 35: '#ffc09c', 33: '#ffc09c',
  23: '#ffd59c', 8: '#ffd59c', 12: '#ffd59c',
  20: '#ffd1d1',
  45: '#ffaa9d',
};

/**
 * Positie van elke poort-blokje binnen het wiel-diagram, als fractie van de
 * afbeeldingsbreedte/-hoogte (het diagram is vierkant, 446×446). Opgemeten op
 * `throat-voices-wheel.png`. De verbindingslijnen vertrekken vanaf deze punten
 * naar de bijhorende kaart, zodat ze — net als in het Figma-design — de poort
 * met zijn rechthoek verbinden.
 */
const HD_WHEEL_GATE_ANCHORS: Readonly<Record<number, readonly [number, number]>> = {
  62: [0.399, 0.336], 23: [0.511, 0.336], 56: [0.621, 0.334],
  16: [0.341, 0.428], 35: [0.684, 0.408],
  20: [0.341, 0.561], 12: [0.684, 0.516],
  31: [0.399, 0.679], 8: [0.500, 0.679], 33: [0.601, 0.679],
  45: [0.684, 0.617],
};

/**
 * Startpunt-offset vanaf het poort-middelpunt, als fractie van de wielbreedte.
 * De lijn begint net buiten het poort-cijfer, op de kaartzijde, zodat ze op de
 * cirkelrand lijkt te starten (geen gat, en niet over het cijfer). De meeste
 * poorten zitten goed op {@link HD_WHEEL_GATE_START_OFFSET_DEFAULT}; bij
 * 16/20/31/45 ligt het cijfer dichter bij het wielmidden, dus daar is een
 * grotere offset nodig (visueel bevestigd door Steven).
 */
const HD_WHEEL_GATE_START_OFFSET_DEFAULT = 0.03;
const HD_WHEEL_GATE_START_OFFSET: Readonly<Record<number, number>> = {
  16: 0.05, 20: 0.05, 31: 0.05, 45: 0.038,
};

/**
 * Vanaf deze viewportbreedte staat het wiel écht naast de kaartkolommen (de
 * drie kolommen vallen eronder samen, zie de media-query in de SCSS). Alleen
 * dan hebben de verbindingslijnen betekenis; smaller laten we ze weg.
 */
const HD_CONNECTOR_MIN_WIDTH = 1001;

const HD_VOICE_GROUPS = [
  { key: 'metamorphic', gates: [62, 23, 56] },
  { key: 'experiment', gates: [16] },
  { key: 'now', gates: [20] },
  { key: 'emotionMotor', gates: [35, 12] },
  { key: 'leadership', gates: [31, 8] },
  { key: 'memory', gates: [33] },
  { key: 'willpower', gates: [45] },
] as const;

type HdVoiceGroupKey = (typeof HD_VOICE_GROUPS)[number]['key'];

interface VoiceCardVm {
  readonly gate: number;
  readonly color: string;
  /** Stem-keynote uit de gates-scope ("Ik weet", "I know", …). */
  readonly voiceKeynote: string;
  /** Voice-eigenschappen uit de gates-scope ("volharden, aandringen / ophouden", …). */
  readonly voiceProperties: string;
  /** Poort-keynotes-lijn ("Overwicht van het kleine, details"). */
  readonly keynotes: string;
  readonly attention: string;
  readonly solution: string;
  /** Circuit-beschrijving onderaan de kaart. */
  readonly circuit: string;
}

interface VoiceGroupVm {
  readonly key: HdVoiceGroupKey;
  readonly title: string;
  readonly subtitle: string;
  readonly attention: string;
  readonly solution: string;
  readonly cards: readonly VoiceCardVm[];
}

/**
 * Eén blok in een kaartkolom: een voice-groep, het wiel, of de keelcentrum-noot.
 * `group` staat als optioneel veld op één interface (i.p.v. een discriminated
 * union) zodat de template-type-check bij `@if (block.group; as grp)` netjes
 * naar `VoiceGroupVm` versmalt.
 */
type ColumnBlockKind = 'group' | 'wheel' | 'note';

interface ColumnBlockVm {
  readonly kind: ColumnBlockKind;
  readonly group?: VoiceGroupVm;
}

interface ColumnVm {
  readonly key: 'left' | 'center' | 'right';
  readonly blocks: readonly ColumnBlockVm[];
}

/** Eén getekende verbindingslijn poort → kaart, in SVG-coördinaten (px). */
interface ConnectorLine {
  readonly gate: number;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly color: string;
}

/**
 * Pagina met de Communicatie (keel & voices) cheat sheet, nagebouwd uit het
 * Figma-design "A3 HD Communicatie - Keel" (node 1122-645, zonder A3-header/
 * branding-footer). Bovenaan de metamorfische/projector-groep (62, 23, 56),
 * daaronder drie doorlopende kolommen rond het centrale wiel-diagram:
 * links 16 / 20 / 31 / 8, centraal het wiel + 33, rechts 35 / 12 / 45 en de
 * keelcentrum-noot. Vanaf het wiel lopen gekleurde verbindingslijnen naar de
 * bijhorende kaart (zie {@link HD_WHEEL_GATE_ANCHORS}).
 *
 * De voice-keynotes en -eigenschappen komen uit de bestaande
 * `human-design/gates`-scope (`gate.voice.*`); de cheat-sheet-specifieke
 * teksten (aandachtspunten, oplossingen, circuit-beschrijvingen, groepstitels)
 * komen uit de `human-design/communication`-scope via
 * {@link HdGateInfoService.cheatSheetTextIn}. Taalwissel via de gedeelde
 * content-taal-picker; nog niet vertaalde EN-velden vallen terug op NL.
 */
@Component({
  selector: 'hd-communication-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-communication-cheat-sheet-page.component.html',
  styleUrl: './hd-communication-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdCommunicationCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly pageTitle = computed(() => this.text('pageTitle'));
  protected readonly pageSubtitle = computed(() => this.text('pageSubtitle'));
  protected readonly throatNote = computed(() => this.text('note'));
  protected readonly wheelImage = 'assets/human-design/communication/throat-voices-wheel.png';
  protected readonly wheelAlt = computed(() => this.text('wheelAlt'));

  /** De getekende verbindingslijnen; leeg tot na de eerste meting/op smalle schermen. */
  protected readonly connectors = signal<readonly ConnectorLine[]>([]);

  private rafId = 0;

  private readonly groups = computed<readonly VoiceGroupVm[]>(() =>
    HD_VOICE_GROUPS.map(group => ({
      key: group.key,
      title: this.text(`groups.${group.key}.title`),
      subtitle: this.text(`groups.${group.key}.subtitle`),
      attention: this.text(`groups.${group.key}.attention`),
      solution: this.text(`groups.${group.key}.solution`),
      cards: group.gates.map(gate => this.card(gate)),
    })),
  );

  /** De volle-breedte groep bovenaan (62, 23, 56). */
  protected readonly metamorphicGroup = computed(() => this.group('metamorphic'));

  /** De drie kaartkolommen rond het wiel, zoals in het Figma-design. */
  protected readonly columns = computed<readonly ColumnVm[]>(() => [
    {
      key: 'left',
      blocks: [
        { kind: 'group', group: this.group('experiment') },
        { kind: 'group', group: this.group('now') },
        { kind: 'group', group: this.group('leadership') },
      ],
    },
    {
      key: 'center',
      blocks: [{ kind: 'wheel' }, { kind: 'group', group: this.group('memory') }],
    },
    {
      key: 'right',
      blocks: [
        { kind: 'group', group: this.group('emotionMotor') },
        { kind: 'group', group: this.group('willpower') },
        { kind: 'note' },
      ],
    },
  ]);

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');

    // Herbereken de verbindingslijnen na de eerste render en bij elke
    // taalwissel (de kaarthoogtes verschuiven mee met de tekst).
    afterNextRender(() => this.initConnectors());
    effect(() => {
      this.lang();
      this.scheduleRecompute();
    });
  }

  protected print(): void {
    window.print();
  }

  protected attentionLabel(): string {
    return this.text('labels.attentionPoint');
  }

  protected solutionLabel(): string {
    return this.text('labels.solution');
  }

  /** `track`-sleutel voor een kolomblok in de template. */
  protected blockKey(block: ColumnBlockVm): string {
    return block.group ? block.group.key : block.kind;
  }

  /** Herbereken zodra het wiel-diagram geladen is (kan ná de layout gebeuren). */
  protected onWheelLoad(): void {
    this.scheduleRecompute();
  }

  private group(key: HdVoiceGroupKey): VoiceGroupVm {
    return this.groups().find(group => group.key === key)!;
  }

  private card(gate: number): VoiceCardVm {
    const voice = this.gateInfo.gateInfoIn(gate, this.lang()).translations.voice;
    return {
      gate,
      color: HD_VOICE_CARD_COLORS[gate],
      voiceKeynote: voice?.keynote ?? '',
      voiceProperties: voice?.properties ?? '',
      keynotes: this.text(`gates.${gate}.keynotes`),
      attention: this.text(`gates.${gate}.attention`),
      solution: this.text(`gates.${gate}.solution`),
      circuit: this.text(`gates.${gate}.circuit`),
    };
  }

  // ─────────────────────────── Verbindingslijnen ───────────────────────────

  /** Zet de observers/listeners op die een herberekening triggeren. */
  private initConnectors(): void {
    const host = this.hostRef.nativeElement;
    this.scheduleRecompute();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.scheduleRecompute());
      const deck = host.querySelector('.hd-communication-cheat-sheet-page__deck');
      if (deck) {
        observer.observe(deck);
      }
      // Ook de kolommen: een reflow die enkel een kolom hoger/lager maakt
      // (bv. taalwissel) verandert de deck-hoogte niet altijd.
      host
        .querySelectorAll('.hd-communication-cheat-sheet-page__column')
        .forEach(column => observer.observe(column));
      this.destroyRef.onDestroy(() => observer.disconnect());
    }

    const onResize = (): void => this.scheduleRecompute();
    window.addEventListener('resize', onResize);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));

    // Herbereken zodra de webfonts geladen zijn (kaartbreedtes verschuiven).
    document.fonts?.ready?.then(() => this.scheduleRecompute()).catch(() => undefined);

    this.destroyRef.onDestroy(() => {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    });
  }

  /** Batcht meerdere triggers tot één meting per frame. */
  private scheduleRecompute(): void {
    if (this.rafId) {
      return;
    }
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0;
      const lines = this.measureConnectors();
      this.zone.run(() => this.connectors.set(lines));
    });
  }

  /** Meet de actuele kaart-/wielposities en bouwt de lijnen. */
  private measureConnectors(): ConnectorLine[] {
    if (typeof window === 'undefined' || window.innerWidth < HD_CONNECTOR_MIN_WIDTH) {
      return [];
    }
    const host = this.hostRef.nativeElement;
    const svg = host.querySelector<SVGSVGElement>('.hd-communication-cheat-sheet-page__connectors');
    const wheel = host.querySelector<HTMLImageElement>('.hd-communication-cheat-sheet-page__wheel-image');
    if (!svg || !wheel) {
      return [];
    }
    const svgRect = svg.getBoundingClientRect();
    const wheelRect = wheel.getBoundingClientRect();
    // Wacht tot het wiel écht is uitgemeten (breedte én hoogte): vóór de
    // afbeelding geladen is, is de hoogte nog 0 en zouden de startpunten
    // bovenaan het wiel plakken.
    if (wheelRect.width < 1 || wheelRect.height < 1 || svgRect.width < 1) {
      return [];
    }

    const lines: ConnectorLine[] = [];
    for (const gateKey of Object.keys(HD_WHEEL_GATE_ANCHORS)) {
      const gate = Number(gateKey);
      const card = host.querySelector<HTMLElement>(`.voice-card[data-gate="${gate}"]`);
      if (!card) {
        continue;
      }
      const [fx, fy] = HD_WHEEL_GATE_ANCHORS[gate];
      const cardRect = card.getBoundingClientRect();
      const boxX = wheelRect.left - svgRect.left + fx * wheelRect.width;
      const boxY = wheelRect.top - svgRect.top + fy * wheelRect.height;
      const cardX = cardRect.left - svgRect.left + cardRect.width / 2;
      const cardY = cardRect.top - svgRect.top + cardRect.height / 2;

      const dx = cardX - boxX;
      const dy = cardY - boxY;
      const dist = Math.hypot(dx, dy);
      if (dist < 1) {
        continue;
      }
      // Start net buiten het poort-cijfer, eindig op de rand van de kaart.
      const offset =
        (HD_WHEEL_GATE_START_OFFSET[gate] ?? HD_WHEEL_GATE_START_OFFSET_DEFAULT) * wheelRect.width;
      const startX = boxX + (dx / dist) * offset;
      const startY = boxY + (dy / dist) * offset;
      const end = this.clipToRect(boxX, boxY, cardX, cardY, cardRect, svgRect);
      lines.push({ gate, x1: startX, y1: startY, x2: end.x, y2: end.y, color: HD_VOICE_CARD_COLORS[gate] });
    }
    return lines;
  }

  /**
   * Snijpunt van het segment (buiten → kaartcentrum) met de kaartrand: het
   * eerste punt waar de lijn de rechthoek binnenkomt.
   */
  private clipToRect(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    rect: DOMRect,
    svgRect: DOMRect,
  ): { x: number; y: number } {
    const left = rect.left - svgRect.left;
    const right = rect.right - svgRect.left;
    const top = rect.top - svgRect.top;
    const bottom = rect.bottom - svgRect.top;
    const dx = x1 - x0;
    const dy = y1 - y0;
    let t = 1;

    if (dx !== 0) {
      for (const edgeX of [left, right]) {
        const candidate = (edgeX - x0) / dx;
        if (candidate > 0 && candidate < t) {
          const y = y0 + candidate * dy;
          if (y >= top - 0.5 && y <= bottom + 0.5) {
            t = candidate;
          }
        }
      }
    }
    if (dy !== 0) {
      for (const edgeY of [top, bottom]) {
        const candidate = (edgeY - y0) / dy;
        if (candidate > 0 && candidate < t) {
          const x = x0 + candidate * dx;
          if (x >= left - 0.5 && x <= right + 0.5) {
            t = candidate;
          }
        }
      }
    }
    return { x: x0 + t * dx, y: y0 + t * dy };
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('communication', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('communication', key, 'nl');
  }
}
